import { useTranslation } from "react-i18next";
import { TableHead } from "@/components/ui/table";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SortDirection } from "@/hooks/useTableSort";

interface SortableTableHeadProps {
  field: string;
  currentField: string | null;
  direction: SortDirection;
  onSort: (field: string) => void;
  children: React.ReactNode;
  className?: string;
}

export function SortableTableHead({
  field,
  currentField,
  direction,
  onSort,
  children,
  className,
}: SortableTableHeadProps) {
  const { t } = useTranslation();
  const isActive = currentField === field;

  const sortLabel = isActive 
    ? direction === 'asc' ? t('table.sortAsc', 'Ordenado ascendente') : t('table.sortDesc', 'Ordenado descendente')
    : t('table.sortNone', 'Haga clic para ordenar');

  return (
    <TableHead
      className={cn(
        "cursor-pointer select-none hover:text-foreground transition-colors group",
        isActive && "text-foreground",
        className
      )}
      onClick={() => onSort(field)}
      aria-sort={isActive ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button 
        className="flex items-center gap-1 w-full h-full text-left"
        aria-label={`${children}. ${sortLabel}`}
      >
        {children}
        {isActive && direction === "asc" && (
          <ArrowUp className="h-3.5 w-3.5 text-primary" />
        )}
        {isActive && direction === "desc" && (
          <ArrowDown className="h-3.5 w-3.5 text-primary" />
        )}
        {!isActive && (
          <ArrowUpDown className="h-3.5 w-3.5 opacity-0 group-hover:opacity-40 transition-opacity" />
        )}
      </button>
    </TableHead>
  );
}
